import { defaultConfig } from 'app/config/environment';
import { ROUTES } from 'app/utils/constants/paths';
import { isStudent } from 'app/utils/users';
import { getLTIContentIcon, isLTI, isLemonadeQuestion, isVideolesson } from 'core';
import { validateLessonItemsItem, validateLessonItemsSection } from 'core/models/lessonItems.model';
import { getIsCourseReferenced, getIsCourseTemplate1, isCourseEditable, isEditorialCourse } from 'core/utils/course';
import { getAssignedStudents, isEditorial } from 'core/utils/user';
import { CONTENT_TYPES, CONTENT_TYPES_NAMES, typeContentsCovers, typeContentsIcons } from 'core/variables/constant';
import { normalizeJob } from 'core/utils/jobs';
import { isImage } from 'core/utils/utils';
import { LESSON_ITEM_PUBLISH_STATUS, LESSON_ITEM_STATUS } from 'core/variables/lessonItems';
import { SESSION_RESOURCE_TYPE } from 'core/variables/sessions';
import moment from 'moment';
import { isDisregardedProgram } from './user';

export const contentIsEvaluable = (type) => {
  return [CONTENT_TYPES.TEST, CONTENT_TYPES.QUESTION, CONTENT_TYPES.VIDEOLESSON].includes(type);
};

/**
 * Comprueba si el tipo de contenido permite editar la categoría y escala de evaluación:
 * Quiz, Tarea, Videolesson, Página HTML (mint-page) y XAPI. El resto de tipos
 * (vídeo, audio, PDF, imagen, link, etc.) no muestran los selectores.
 * Ojo: un XAPI solo cuenta en el cuaderno si include_in_gradebook no es false; ese
 * matiz es del ítem (no del tipo) y lo aplica cada consumidor que lo necesite.
 * @param {string} type - content_type_guid del ítem
 * @returns {boolean}
 */
export const canEditItemEvaluation = (type) => {
  return [
    CONTENT_TYPES.TEST,
    CONTENT_TYPES.QUESTION,
    CONTENT_TYPES.VIDEOLESSON,
    CONTENT_TYPES.MINT_PAGE,
    CONTENT_TYPES.MINT_PAGE_DUAL,
    CONTENT_TYPES.XAPI,
  ].includes(type);
};

export const contentIsActivity = (type) => {
  return [CONTENT_TYPES.TEST, CONTENT_TYPES.QUESTION, CONTENT_TYPES.VIDEOLESSON, CONTENT_TYPES.XAPI].includes(type);
};

/**
 * Content-types cuya descripción se le muestra al estudiante en Power Lesson.
 *
 * Hoy coincide con `contentIsActivity`, pero es una decisión de producto deliberada y no
 * debe seguir los cambios de esa utilidad. PLENO (`MINT_PAGE`) queda fuera a propósito: su
 * pantalla es solo el código de acceso, no hay portada donde encajar la descripción. SCORM
 * ni siquiera pasa por el header del estudiante (`LessonViewPowerContent.js`).
 *
 * @param {string} type - content_type del ítem
 * @returns {boolean}
 */
export const contentSupportsDescription = (type) => {
  return [CONTENT_TYPES.TEST, CONTENT_TYPES.QUESTION, CONTENT_TYPES.VIDEOLESSON, CONTENT_TYPES.XAPI].includes(type);
};

export const hasSmartLink = (type) => {
  return [CONTENT_TYPES.TEST, CONTENT_TYPES.QUESTION].includes(type);
};

export const noShowContentInSmartLink = (type) => {
  return [
    CONTENT_TYPES.TEST,
    CONTENT_TYPES.QUESTION,
    CONTENT_TYPES.VIDEOLESSON,
    CONTENT_TYPES.MINT_PAGE,
    CONTENT_TYPES.MINT_PAGE_DUAL,
    CONTENT_TYPES.MINT,
  ].includes(type);
};

export const contentHasProgress = (type, lessonItem) => {
  return lessonItem ? [CONTENT_TYPES.XAPI].includes(type) && !!lessonItem?.include_in_gradebook : [CONTENT_TYPES.XAPI].includes(type);
};

export const getIconByType = ({ content_data, type }) => {
  let icon = type || 'CTTY_09';

  if (isLTI(type)) {
    icon = getLTIContentIcon(content_data);
  }

  return typeContentsIcons[icon];
};

export const getIconByContentType = (content_type) => {
  return content_type ? typeContentsIcons[content_type] : 'doc';
};

/**
 * Devuelve las props de estilo del IconBubble de un lesson item según su estado
 * de publicación: verde (alpha/primary) si está publicado, gris (gray/grey-04)
 * si es borrador. Centraliza el patrón para no repetir el ternario en cada layout.
 * @param {boolean} isPublished
 * @returns {{ background: string, color: string }}
 */
export const getIconBubbleStatusStyle = (isPublished) => {
  return isPublished ? { background: 'alpha', color: 'primary' } : { background: 'gray', color: 'grey-04' };
};

/**
 * Resuelve el icono a mostrar para un lesson item.
 * Los plenos usan siempre el icono 'pleno' del DS (su item.icon no es fiable).
 * Para el resto prioriza el icono de imagen (didactic types), después el mapeo
 * por content type y como último recurso el icono ya presente en el item, con
 * 'doc' de fallback final (registrado en el DS v2).
 * @param {Object} item - Lesson item con icon y content_type/content_type_guid
 * @returns {string} - Nombre de icono del DS o URL de imagen
 */
export function getLessonItemIconName(item) {
  const contentType = item?.content_type || item?.content_type_guid;
  if (contentType === CONTENT_TYPES.PLENO) return typeContentsIcons[CONTENT_TYPES.PLENO];
  if (item?.icon && isImage(item.icon)) return item.icon;
  return typeContentsIcons[contentType] || item?.icon || 'doc';
}

export const getTypeNameByContentType = (content_type) => {
  return content_type ? CONTENT_TYPES_NAMES[content_type] : 'Instructions';
};

export const getTypeName = ({ content_is_evaluable, freezed_amount_questions, t, test_amount_questions, type, typeItem }) => {
  const typeComponent = typeItem || 'content';

  const getEvaluativeLabel = (baseLabel) => {
    return `${content_is_evaluable ? t(`assigments:Evaluative ${baseLabel}`) : t(`assigments:Formative ${baseLabel}`)}`.toUpperCase();
  };

  if (isLTI(type)) {
    return t('lesson:LTI Content').toUpperCase();
  }

  if (['scorm', 'content', 'xapi'].includes(typeComponent)) {
    return t(`types-resources:${type}`).toUpperCase();
  }

  if (isVideolesson(type)) {
    return getEvaluativeLabel('videolesson');
  }

  if (isLemonadeQuestion(type)) {
    return getEvaluativeLabel('assignment');
  }

  const quizLabel = getEvaluativeLabel('quizz');
  const questionCount = freezed_amount_questions || test_amount_questions;
  return `${quizLabel}: ${t('lesson:X questions', { count: questionCount })}`;
};

export const canUseTangerineViewer = (content_type) => {
  return [
    CONTENT_TYPES.AUDIO,
    CONTENT_TYPES.FIXED_LAYOUT,
    CONTENT_TYPES.IMAGE,
    CONTENT_TYPES.OFFICE,
    CONTENT_TYPES.PDF,
    CONTENT_TYPES.VIDEO,
    CONTENT_TYPES.XAPI,
  ].includes(content_type);
};

export const canUseTangerineViewerInPreview = (content_type) => {
  return [
    CONTENT_TYPES.AUDIO,
    CONTENT_TYPES.FIXED_LAYOUT,
    CONTENT_TYPES.IMAGE,
    CONTENT_TYPES.OFFICE,
    CONTENT_TYPES.PDF,
    CONTENT_TYPES.VIDEO,
    CONTENT_TYPES.ZIP,
  ].includes(content_type);
};

export const isDocumentContent = (content_type) => {
  return [CONTENT_TYPES.PDF, CONTENT_TYPES.OFFICE, CONTENT_TYPES.XAPI, CONTENT_TYPES.FIXED_LAYOUT, CONTENT_TYPES.ZIP].includes(content_type);
};

export const isDueDate = (content_type) => {
  return [CONTENT_TYPES.TEST, CONTENT_TYPES.QUESTION, CONTENT_TYPES.VIDEOLESSON].includes(content_type);
};

/**
 * Gets the projection route based on the content type.
 * This function determines the appropriate route for projecting/displaying different types of content
 * by replacing the contentGuid parameter in the route template with the actual content type.
 *
 * @param {string} content_type - The type of content to get the projection route for
 * @returns {string} The formatted route for projecting the content
 */
export const getRouteProjectionByContentType = (content_guid, content_type) => {
  const routeTest = content_type === CONTENT_TYPES.TEST ? ROUTES.PROJECTION_TEST : '';
  const routeQuestion = content_type === CONTENT_TYPES.QUESTION ? ROUTES.PROJECTION_QUESTION : '';
  const routeVideoLesson = content_type === CONTENT_TYPES.VIDEOLESSON ? ROUTES.PROJECTION_VIDEOLESSON : '';
  const route = routeTest || routeQuestion || routeVideoLesson;

  return route.replace(':contentGuid', content_guid);
};

export const getUsePdfTron = (content_type) => {
  return [CONTENT_TYPES.PDF, CONTENT_TYPES.OFFICE, CONTENT_TYPES.XAPI, CONTENT_TYPES.FIXED_LAYOUT, CONTENT_TYPES.ZIP].includes(content_type);
};

export const isDocument = (content_type) => {
  return [CONTENT_TYPES.PDF, CONTENT_TYPES.OFFICE, CONTENT_TYPES.FIXED_LAYOUT].includes(content_type);
};

export function filterLessonItemValues(lessonItem) {
  const filteredLessonItem = {
    guid: lessonItem.guid,
    name: lessonItem.name,
    description: lessonItem.description,
    author_role_guid: lessonItem.author_role_guid,
    content_guid: lessonItem.content_guid,
    content_data: lessonItem.content_data,
    content_type_guid: lessonItem.content_type_guid,
    content_is_teacher_only: lessonItem.content_is_teacher_only,
    teacher_notes: lessonItem.teacher_notes,
    item_for: lessonItem.item_for,
    item_for_users: lessonItem.item_for_users,
    item_jobs_total: lessonItem.item_jobs_total,
    item_jobs_completed: lessonItem.item_jobs_completed,
    jobs: lessonItem.jobs,
    lesson_guid: lessonItem.lesson_guid,
    deliver_at: lessonItem.deliver_at,
    ranking_scale: lessonItem.ranking_scale,
    evaluation_category: lessonItem.evaluation_category,
    evaluation_scale: lessonItem.evaluation_scale,
    published_at: lessonItem.published_at,
    is_visible: lessonItem.status,
    status: lessonItem.status,
    can_deliver_late: lessonItem.can_deliver_late,
    session: lessonItem.session
      ? lessonItem.session.map((session) => ({
          order: session.order,
          resource_type: session.resource_type,
        }))
      : [],
  };

  return filteredLessonItem;
}

export const getLessonItemPath = (contentType, isStudent) => {
  if (isStudent && [CONTENT_TYPES.TEST, CONTENT_TYPES.QUESTION, CONTENT_TYPES.VIDEOLESSON].includes(contentType)) {
    return ROUTES.LESSON;
  }

  if (!isStudent && [CONTENT_TYPES.TEST, CONTENT_TYPES.QUESTION, CONTENT_TYPES.VIDEOLESSON].includes(contentType)) {
    return ROUTES.RESULTS_ASSESSMENT;
  }

  return ROUTES.LESSON;
};

export function getAssignedUsersToLessonItem(lessonItem, course) {
  const users = course?.users?.filter((user) => isStudent(user))?.map((user) => ({ ...user, guid: user.person_guid })) || [];

  if (lessonItem?.item_for === 'all') {
    return users;
  }

  if (!lessonItem?.item_for_users) {
    return [];
  }

  return users.filter((user) => lessonItem.item_for_users.includes(user.person_guid));
}

export function addUserInfoToJob(jobs = [], users = [], lessonItemGuid, courseGuid) {
  return users
    .filter((user) => isStudent(user))
    ?.map((user) => {
      const job = normalizeJob(jobs.find((candidate) => candidate.person_guid === user.person_guid)) ?? {};
      return {
        ...user,
        ...job,
        fullname: user?.name + ' ' + user.lastname,
        status: job.status || 'pending',
        lesson_item_guid: lessonItemGuid,
        course_guid: courseGuid,
        student_guid: user.person_guid,
        student_name: user.name,
        student_lastname: user.lastname,
        student_avatar: user.avatar,
      };
    });
}

export function getSectionIcon(section) {
  return !section.is_customized ? defaultConfig.BASE_URL_ + 'app/favicon.ico' : null;
}

export function updateSections(items, client, isStudent) {
  const updatedSections = items
    .filter((item) => !item.lesson_item_parent_guid)
    .map((item) => validateLessonItemsSection(item, client, isStudent))
    .sort((a, b) => a.order - b.order);

  return updatedSections;
}

export function updateSectionsForStudent(items) {
  const updatedSections = items
    .filter((item) => !item.lesson_item_parent_guid)
    .map((item) => validateLessonItemsSection(item))
    .sort((a, b) => a.order - b.order);

  return updatedSections;
}

export function updateModuleSectionForStudent(items) {
  const updatedModuleSection = items
    .filter((item) => item.lesson_item_parent_guid && item.session.some((session) => session.session_resource_type === SESSION_RESOURCE_TYPE.LESSON))
    .map((item) => validateLessonItemsItem(item))
    .sort((a, b) => a.order - b.order);
  return updatedModuleSection;
}

export function updateLessonItemsForStudent(items, client) {
  const updatedLessonItems = items
    .filter(
      (item) =>
        (item.lesson_item_parent_guid && !item.session.some((session) => session.session_resource_type === SESSION_RESOURCE_TYPE.LESSON)) || item.content_type
    )
    .map((item) => validateLessonItemsItem(item, client))
    .sort((a, b) => a.order - b.order);
  return updatedLessonItems;
}

export function updateLessonItems(items) {
  const updatedLessonItems = items
    .filter((item) => item.lesson_item_parent_guid || item.content_type)
    .map((item) => validateLessonItemsItem(item))
    .sort((a, b) => a.order - b.order);
  return updatedLessonItems;
}

export function getLessonItemImageCover(type, deliver_at, can_deliver_late, isClosed, status, isGradeable) {
  if (
    (status === LESSON_ITEM_STATUS.PENDING && !can_deliver_late && moment(deliver_at).isBefore(moment())) ||
    (isClosed && (status === LESSON_ITEM_STATUS.PENDING || !status) && isGradeable)
  ) {
    return 'cover-card-out-of-time';
  }

  if (status === LESSON_ITEM_STATUS.DELIVERED && (type === CONTENT_TYPES.QUESTION || type === CONTENT_TYPES.TEST)) {
    return typeContentsCovers[type + '_DONE'] || 'cover-card-quizz-done';
  }

  return typeContentsCovers[type] || 'cover-card-quizz';
}

export function getLessonItemType(type) {
  return CONTENT_TYPES_NAMES[type] || 'Instructions';
}

//LESSON ITEMS USER ACTIONS //
/**
 * Comprobamos si el usuario puede editar opciones del ítem: Assign to, Status -> Tiene que estar en el contexto de clase
 * @param {*} item
 * @param {*} course
 * @returns {boolean} - True si el usuario puede editar opciones del ítem
 */
export function canEditItemOptions(course, jobsDelivered) {
  //accede desde la biblioteca, no pertenece a una clase y no han entregado alumnos
  return !isDisregardedProgram(course) && !jobsDelivered;
}

/**
 * El chip de estado de la cabecera no deja cambiar lo que la configuración oculta: `canEditStatus` es el
 * mismo valor que recibe GeneralSettingsSidebar (TAN-11307).
 */
export function isStatusTagReadOnly(course, canEditStatus) {
  return Boolean(course?.isReadOnly) || !canEditStatus;
}

/**
 * Verifica si el usuario tiene permisos para modificar el ítem.
 * `is_customized` significa "personalización del docente sobre un programa editorial", no "creado por
 * el docente": el backend solo lo pone a 1 en programas `template-1` y sus clases; en cualquier otro curso
 * (propio `no-referenced`, `template-2` o clase sobre `template-2`, que es lo que crea la plantilla
 * Standard) los ítems del docente llegan siempre con 0 (TAN-10510). Por eso fuera de un curso editorial basta
 * ser el creador. Mientras el curso no ha cargado (`useCourse` arranca con `{}`) no hay `type` y se aplica
 * la regla estricta, que es la anterior.
 * @param {Object} item - El ítem de la lección
 * @param {Object} currentUser - El usuario actual
 * @param {Object} course - El curso en cuyo contexto se evalúa la propiedad del ítem. No es opcional:
 *   omitirlo revierte en silencio a la regla estricta. Puede llegar vacío (`{}`) mientras `useCourse` carga,
 *   y en ese caso se aplica la regla estricta hasta que el curso tenga `type`.
 * @returns {boolean} - True si el usuario puede modificar el ítem
 */
export function isItemOwnedByUser(item, currentUser, course) {
  const isCreator = Boolean(item?.created_by) && item.created_by === currentUser?.guid;
  if (!isCreator) return false;
  const hasCourseType = Boolean(course?.type);
  if (hasCourseType && !isEditorialCourse(course)) return true;
  return Boolean(item?.is_customized);
}

/**
 * Verifica si el curso tiene permisos de edición
 * @param {Object} course - El curso
 * @param {Object} currentUser - El usuario actual
 * @returns {boolean} - True si el curso puede ser editado
 */
export function hasCourseEditPermissions(course, currentUser) {
  return isCourseEditable(course) || getIsCourseTemplate1(course) || isEditorial(currentUser) || getIsCourseReferenced(course);
}

/**
 * Verifica si hay trabajos entregados para el ítem
 * @param {Object} item - El ítem de la lección
 * @returns {boolean} - True si hay trabajos entregados o evaluados
 */
export function hasDeliveredJobs(item) {
  return (
    (item?.jobs_delivered > 0 || item?.jobs_evaluated > 0 || item?.items_jobs_completed > 0 || item?.item_jobs_completed > 0) &&
    contentIsEvaluable(item.content_type || item.content_type_guid)
  );
}

/**
 * Verifica si una sección tiene actividades con trabajos entregados
 * @param {Array} items - Items de la sección
 * @returns {boolean} - True si alguna actividad tiene trabajos entregados
 */
export function sectionHasDeliveredJobs(items) {
  return Array.isArray(items) && items.some(hasDeliveredJobs);
}

/**
 * Verifica si el ítem puede ser movido a otra sección
 * @param {Object} item - El ítem de la lección
 * @param {Array} sectionItems - Lista de secciones disponibles
 * @param {Array} availableSections - Secciones disponibles para mover
 * @param {Object} currentUser - El usuario actual
 * @param {Object} course - El curso en cuyo contexto se evalúa la propiedad del ítem (ver `isItemOwnedByUser`:
 *   omitirlo revierte en silencio a la regla estricta)
 * @returns {boolean} - True si el ítem puede ser movido
 */
export function canMoveItemToSection(item, sectionItems, availableSections, currentUser, course) {
  const hasMultipleSections = Array.isArray(sectionItems) && sectionItems.length > 1;
  const isOwnedByUser = isItemOwnedByUser(item, currentUser, course);
  const parentSection = sectionItems.find((section) => section.lesson_item_guid === item.lesson_item_parent_guid);
  const isParentSectionCustomized = parentSection?.is_customized;

  return hasMultipleSections && isParentSectionCustomized && isOwnedByUser && availableSections.length > 0;
}

/**
 * Obtiene la lista de estudiantes asignados al ítem de la lección
 * @param {Object} course - El curso
 * @param {Object} lessonItem - El ítem de la lección
 * @returns {Array} - Lista de estudiantes
 */
export function getStudentsList(course, lessonItem) {
  // Pass course.users directly like MintLessonItemView does (line 60)
  // addUserInfoToJob will filter by isStudent internally
  const enrichedUsers = addUserInfoToJob(lessonItem?.jobs || [], course?.users, lessonItem?.guid, course?.guid);

  // Filter by item_for_students if it exists
  const filteredUsers =
    lessonItem?.item_for_students?.length > 0
      ? enrichedUsers.filter((user) => lessonItem.item_for_students.some((itemForStudent) => itemForStudent.guid === user.guid))
      : enrichedUsers;

  // Always apply getAssignedStudents to set disabled property based on status
  return getAssignedStudents(filteredUsers, lessonItem?.item_for_users?.length > 0 ? lessonItem?.item_for_users : false);
}

/**
 * Obtiene el tooltip apropiado para la edición según el estado del ítem
 * @param {boolean} jobsDelivered - Si hay trabajos entregados
 * @returns {string} - Texto del tooltip
 */
export const getEditTooltip = (jobsDelivered, t) => {
  if (jobsDelivered) {
    return t('actions:Jobs delivered tooltip');
  }
  return t('actions:You are not the owner of this item');
};

export const contentIsTeacherOnly = (item) => {
  return item?.content_is_teacher_only || item?.is_teacher_only;
};

export const getItemOptionsDefault = (item, user, course, t, onClickActions, hideSettings = false) => {
  const isOwnedByUser = isItemOwnedByUser(item, user, course);
  // Verificar permisos básicos
  if (!isOwnedByUser && !hasCourseEditPermissions(course, user)) {
    return [];
  }

  const jobsDelivered = hasDeliveredJobs(item);
  const canEditItem = isOwnedByUser && !jobsDelivered;

  let options = [
    // Edit option
    {
      name: t('actions:Edit'),
      icon: 'pencil',
      disabled: !canEditItem,
      ...(!canEditItem && { tooltip: getEditTooltip(jobsDelivered, t) }),
      onClick: () => onClickActions.onClickEdit(item),
    },
    // Delete option
    {
      name: t('actions:Delete'),
      icon: 'trash',
      disabled: !canEditItem,
      ...(!canEditItem && { tooltip: getEditTooltip(jobsDelivered, t) }),
      onClick: () => onClickActions.onClickDelete(item),
    },
  ];

  if (!hideSettings) {
    return [
      // Assign to option
      {
        id: 'assignTo',
        name: t('lesson:Visible by menu option'),
        icon: 'user',
        onClick: (option, e) => onClickActions.onClickOpenStudentsList(e),
      },
      // Visibility option
      {
        name: item.status === LESSON_ITEM_PUBLISH_STATUS.PUBLISHED ? t('actions:Make not visible') : t('actions:Make visible'),
        icon: item.status === LESSON_ITEM_PUBLISH_STATUS.PUBLISHED ? 'visible-off' : 'visible',
        disabled: item.status === LESSON_ITEM_PUBLISH_STATUS.PUBLISHED && jobsDelivered,
        ...(item.status === LESSON_ITEM_PUBLISH_STATUS.PUBLISHED && jobsDelivered && { tooltip: getEditTooltip(jobsDelivered, t) }),
        onClick: () => onClickActions.onClickChangeStatus(item),
      },
      ...options,
    ];
  }

  return options;
};
